import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import type { AppDispatch } from 'config/redux'
import type { RootState } from 'constants/interfaces'
import keys from 'constants/keys'
import { getCampaignById } from 'redux/actions/campaigns'
import { Campaign } from 'redux/reducers/campaigns'

interface UseLinkedCampaignProps {
  ref_id?: string
}

export const useLinkedCampaign = ({ ref_id }: UseLinkedCampaignProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch: AppDispatch = useDispatch()

  const activeGroupID = useSelector((s: RootState) => s.config.activeGroupID, shallowEqual)
  const campaigns = useSelector((s: RootState) => s.campaigns.campaigns, shallowEqual)
  const kpis = useSelector((s: RootState) => s.ranking.kpisList, shallowEqual)

  const campaignId = useMemo(() => {
    if (!ref_id) return null
    const i = ref_id.indexOf('campaigns-')
    return i === -1 ? null : ref_id.slice(i + 'campaigns-'.length)
  }, [ref_id])

  const isPostLinkedToCampaign = !!campaignId

  const cached = useMemo<Campaign | null>(() => {
    if (!campaignId) return null
    return campaigns.find((c) => c.oid === campaignId) ?? null
  }, [campaigns, campaignId])

  const [isLoading, setIsLoading] = useState(false)
  const [localCampaign, setLocalCampaign] = useState<Campaign | null>(null)

  useEffect(() => {
    if (!campaignId || cached) {
      setLocalCampaign((prev) => (cached ? null : prev))
      return
    }

    let alive = true
    ;(async () => {
      setIsLoading(true)
      const res = await dispatch(getCampaignById(campaignId))
      if (alive && res && !cached) {
        setLocalCampaign(res)
      }
      setIsLoading(false)
    })()

    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, cached, dispatch])

  const campaign = cached ?? localCampaign

  const mutatedCampaign = useMemo(() => {
    if (!campaign) return null
    return { ...campaign, campaign_id: campaign.oid, campaign_type: campaign.type }
  }, [campaign])

  const kpiId = useMemo(() => {
    if (!campaign) return null
    return kpis.find((kpi) => kpi?.campaign_data?.oid === campaign.oid)?.id
  }, [campaign, kpis])

  const handleNavigateToCampaign = useCallback(() => {
    if (!campaignId || !activeGroupID) return
    navigate(keys.ROUTE_NAMES.CAMPAIGN_DETAIL(campaignId, activeGroupID), {
      state: {
        ...mutatedCampaign,
        fromPage: location.pathname,
        kpiId,
      },
    })
  }, [campaignId, activeGroupID, navigate, mutatedCampaign, location.pathname, kpiId])

  return { campaign, handleNavigateToCampaign, isPostLinkedToCampaign, isLoading }
}
